import classNames from 'classnames';
import { useCallback, useMemo, useRef } from 'react';
import Button from '../Button';
import FlexContainer from '../FlexContainer';
import styles from './SelectButtons.module.scss';

/**
 * SelectButtons
 *
 * @return {JSX.Element}
 */
const SelectButtons = ({
  className,
  options,
  value,
  onChange,
  multiple,
  name,
  allButton,
  allButtonTitle = 'All',
  required,
  disabled,
}) => {
  const containerRef = useRef(null);
  const isEmptyValue = useMemo(
    () => typeof value === 'undefined' || value.length === 0,
    [value]
  );
  const isActiveItem = useCallback(
    (i) => (multiple ? value.includes(i.key) : value === i.key),
    [multiple, value]
  );

  const handleChange = (o, isActive) => () => {
    if (multiple) {
      const newValue = isActive
        ? value.filter((v) => v !== o.key)
        : [...value, o.key];
      onChange({ target: { value: newValue, name } });
    } else {
      onChange({ target: { value: o.key, name } });
    }
  };

  const handleReset = () =>
    onChange({ target: { value: multiple ? [] : '', name } });

  const handleFocus = () => {
    containerRef.current?.querySelector('button')?.focus();
  };

  return (
    <FlexContainer
      className={classNames(
        styles.SelectButtons,
        className,
        `${SelectButtons.displayName}`
      )}
      gap
      wrap
      ref={containerRef}
    >
      {allButton ? (
        <Button
          reverse={isEmptyValue}
          onClick={handleReset}
          disabled={disabled}
        >
          {allButtonTitle}
        </Button>
      ) : null}
      {options.map((o) => {
        const isActive = isActiveItem(o);
        return (
          <Button
            key={o.key}
            className={classNames(styles.Item)}
            reverse={isActive}
            onClick={handleChange(o, isActive)}
            disabled={disabled}
          >
            {o.value}
          </Button>
        );
      })}
      <select
        name={name}
        multiple={multiple}
        required={required}
        className={styles.SelectHidden}
        tabIndex="-1"
        value={value}
        onChange={() => {}}
        onFocus={handleFocus}
        disabled={disabled}
      >
        <option value="" />
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.value}
          </option>
        ))}
      </select>
    </FlexContainer>
  );
};

SelectButtons.displayName = 'SelectButtons';

export default SelectButtons;
